/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Utils for Response Screen of Claim Status
 */
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import { CLAIM_STATUS_CODES_MAPPING } from 'app-constants/claims';
import { YEAR_MONTH_DATE_FORMAT } from 'app-constants/index';

const dataKeys = {
	payerIds: 'payerIds',
	identifier: 'identifier',
	patientName: 'patientName',
	dateOfService: 'serviceDates',
	claimNumber: 'claimNumber',
	dateOfBirth: 'dob',
	// custom
	startDate: 'startDate',
	endDate: 'endDate',
	// filter params
	providerIds: 'providerIds',
	status: 'status',
	claimAmount: 'claimAmount',
	claimAge: 'claimAge',
	sortBy: 'sortBy',
	locationId: 'locationId',
};

const dataKeyLabels = {
	payerIds: 'Payer',
	identifier: 'Patient Identifier',
	patientName: 'Patient Name',
	serviceDates: 'Service Dates',
	claimNumber: 'Claim Number',
	dob: 'Date of Birth',
	// filter params
	providerIds: 'Provider/Organization',
	status: 'Status',
	claimAmount: 'Billed Amount',
	claimAge: 'Claim Age',
	sortBy: 'Sort By',
	locationId: 'Service Location',
};

const mapClaimStatusLabelToCodes = (labels?: string[]): string[] => {
	return _.flatMap(labels, (label) => _.get(CLAIM_STATUS_CODES_MAPPING, label));
};

const getApiParams = ({ advancedSearchParams, filterParams }: ClaimStatusRequests.IAppliedParams): ClaimStatusRequests.IApiParams => {
	// claim age is a custom field in ui , shouldn't be sent to api
	let apiParams: ClaimStatusRequests.IApiParams = {
		...(advancedSearchParams || {}),
		...(_.omit(filterParams, dataKeys.claimAge) || {}),
		status: mapClaimStatusLabelToCodes(filterParams?.status),
	};

	// service date computed from claim age
	const claimAgeInDays = _.toNumber(filterParams?.claimAge);
	if (claimAgeInDays >= 0) {
		apiParams = {
			...apiParams,
			serviceDates: {
				startDate: dateParser(new Date(0)).format(YEAR_MONTH_DATE_FORMAT),
				endDate: dateParser().subtract(claimAgeInDays, 'days').format(YEAR_MONTH_DATE_FORMAT),
			},
		};
	}

	// omit empty filters
	return _.omitBy(apiParams, _.isEmpty);
};

export { dataKeys, dataKeyLabels, mapClaimStatusLabelToCodes, getApiParams };
